import React, { useRef, useState } from 'react';
import QrReader from 'react-qr-reader';

import { ContentPreloader, Icon } from '..';
import { AccessPointFragment as AccessPoint } from '../../apollo';
import { colors } from '../../assets/styles';
import UnlockDrawer from '../UnlockDrawer';
import styles from './styles';

export interface QRScannerProps {
  headerText?: string | null;
  headerSubtext?: string | null;
  onClickBack: () => void;
  onFinishScanning?: (accessPointId: string) => void;
  isPropertyMapVisible?: boolean;
  expiresAt?: string | null;
}

interface Reader extends QrReader {
  els: {
    input: {
      value: string;
    },
  };
}

const QRScanner: React.FC<QRScannerProps> = (props) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedAccessPointId, setScannedAccessPointId] = useState<AccessPoint['accessPointId'] | null>(null);
  const [scanError, setScanError] = useState(false);
  const [useLegacyMode, setUseLegacyMode] = useState(false);
  const [isOpeningModule, setIsOpeningModule] = useState(true);

  const readerRef = useRef<Reader>(null);
  const { isPropertyMapVisible, onClickBack, onFinishScanning } = props;

  return (
    <>
      <div className={`${styles.container} ${isOpeningModule ? styles.qrCodeScanOpen : styles.qrCodeScanClose}`}>
        <div className={styles.header}>
          <div
            className={styles.headerLeft}
            onClick={() => {
              setIsOpeningModule(false);
              setTimeout(() => {
                onClickBack();
              }, 300);
            }}
          >
            <div className={styles.headerLeftIcon}>
              <Icon.ArrowLeft color={colors.white} />
            </div>
            <div className={styles.headerLeftText}>
              Back
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.headerRightText}>
              {props.headerText}
            </div>
            <div className={styles.headerRightSubtext}>
              {props.headerSubtext}
            </div>
          </div>
        </div>
        <div className={styles.scannerContainer}>
          {!useLegacyMode && (
            <div className={styles.scannerLoader}>
              <ContentPreloader
                background="transparent"
                zIndex={0}
              />
            </div>
          )}
          <QrReader
            className={styles.scanner}
            ref={readerRef}
            delay={1000}
            facingMode="environment"
            showViewFinder={false}
            legacyMode={useLegacyMode}
            onLoad={() => {
              setIsScanning(true);
            }}
            onError={(err) => {
              setUseLegacyMode(true);
              if (readerRef && readerRef.current) {
                readerRef.current.openImageDialog();
              }
            }}
            onScan={(data) => {
              if (!isScanning || scannedAccessPointId) return;

              if (data) {
                if (
                  // Match url like:
                  // - https://access.stratisiot.com/chirp/entry/{ID}
                  // - https://access.test.stratisiot.com/chirp/entry/{ID}
                  // - https://access.sandbox.stratisiot.com/chirp/entry/{ID}
                  // - https://access.dev.stratisiot.com/chirp/entry/{ID}
                  data.match(/access\.((test|sandbox|dev)\.|)stratisiot\.com\/chirp\/entry\/.+$/)
                  // Match url like: https://access.chirpsystems.com/entry/{ID}
                  || data.match(/access\.chirpsystems\.com\/entry\/.+$/)
                ) {
                  // Match all characters from "entry/" until end of string
                  const matches = data.match(/entry\/(.+)$/);

                  if (matches && matches.length > 0) {
                    setScanError(false);
                    const accessPointIdFromMatch = matches[1];
                    setIsScanning(false);
                    setScannedAccessPointId(accessPointIdFromMatch);
                    if (isPropertyMapVisible) {
                      onFinishScanning && onFinishScanning(accessPointIdFromMatch);
                    }
                  } else {
                    setScanError(true);
                  }
                } else {
                  setScanError(true);
                }
              }
            }}
          />
          <div
            className={styles.scannerOverlay}
            onClick={() => {
              if (useLegacyMode) {
                if (readerRef && readerRef.current) {
                  readerRef.current.els.input.value = '';
                  readerRef.current.openImageDialog();
                }
              }
            }}
          >
            {useLegacyMode ? (
              // LEGACY MODE
              <div className={styles.scannerLegacyText}>
                {!scanError
                  ? 'Tap to capture or select picture of QR code'
                  : 'Unable to detect valid QR code. Tap to try again'
                }
              </div>
            ) : (
              // NORMAL MODE
              <div className={styles.scannerOverlayBoxWrapper}>
                <div className={styles.scannerOverlayBox}>
                  <div className={styles.scannerOverlayText}>
                    {!scanError
                      ? 'Scan Your Code'
                      : 'Unrecognized QR code. Please scan again'
                    }
                  </div>
                </div>
                <div className={styles.scannerOverlayBoxInnerWrapper}>
                  <div className={styles.scannerOverlayBoxInner} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isPropertyMapVisible && scannedAccessPointId &&
        <UnlockDrawer
          accessPointId={scannedAccessPointId}
          onClickClose={onClickBack}
          onFinish={() => {
            setIsScanning(true);
            setScannedAccessPointId(null);
          }}
        />
      }
    </>
  );
};

export default QRScanner;
